import { useEffect, useState } from "react";
import { toZonedTime } from "date-fns-tz";

export type TimestampSnapTo = "second" | "minute" | "hour";

export interface UseCurrentTimestampOptions {
  pollingRateInMs?: number;
  snapTo?: TimestampSnapTo;
  timezone?: string;
}

const getMsUntilNextSnap = (snapTo: TimestampSnapTo, from: Date): number => {
  const next = new Date(from);
  next.setMilliseconds(0);
  if (snapTo === "second") {
    next.setSeconds(next.getSeconds() + 1);
  } else if (snapTo === "minute") {
    next.setSeconds(0);
    next.setMinutes(next.getMinutes() + 1);
  } else {
    next.setSeconds(0);
    next.setMinutes(0);
    next.setHours(next.getHours() + 1);
  }
  return next.getTime() - from.getTime();
};

const getCurrentTimestamp = (timezone: string | undefined): Date => {
  const now = new Date();
  return timezone ? toZonedTime(now, timezone) : now;
};

export const useCurrentTimestamp = ({
  pollingRateInMs = 1000 * 60,
  snapTo,
  timezone,
}: UseCurrentTimestampOptions = {}): Date => {
  const [timestamp, setTimestamp] = useState(() => getCurrentTimestamp(timezone));

  useEffect(() => {
    if (pollingRateInMs === 0 || pollingRateInMs === Infinity) return;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const startPolling = () => {
      setTimestamp(getCurrentTimestamp(timezone));
      intervalId = setInterval(() => setTimestamp(getCurrentTimestamp(timezone)), pollingRateInMs);
    };

    const initialDelay = snapTo ? getMsUntilNextSnap(snapTo, new Date()) : 0;
    const timeoutId = setTimeout(startPolling, initialDelay);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [pollingRateInMs, snapTo, timezone]);

  return timestamp;
};
